/**
 * Exercise Formatters Utility
 * 
 * Utility centralizzata per gestire la formattazione delle unità di misura
 * degli esercizi (ripetizioni, secondi, minuti) in base al tipo di esercizio.
 * 
 * WEBAPP-004: Implementazione utility per mostrare secondi/minuti per isometrici/cardio
 * 
 * @version 1.0.0
 * @created 21/01/2026
 */

/**
 * Tipi di esercizio supportati
 */
export const EXERCISE_TYPES = {
  NORMAL: 'normale',
  ISOMETRIC: 'isometrico',
  CARDIO: 'cardio'
};

/**
 * Determina il tipo di esercizio in base ai flag
 * @param {object} exercise - Oggetto esercizio con flag is_cardio, is_isometric, is_isometric_scheda
 * @returns {string} Tipo esercizio (NORMAL, ISOMETRIC, CARDIO)
 */
export const getExerciseType = (exercise) => {
  if (!exercise) return EXERCISE_TYPES.NORMAL;
  
  // Cardio ha priorità
  if (exercise.is_cardio === 1 || exercise.is_cardio === true) {
    return EXERCISE_TYPES.CARDIO;
  }
  
  // Isometrico (supporta sia is_isometric che is_isometric_scheda)
  if (exercise.is_isometric === 1 || exercise.is_isometric === true ||
      exercise.is_isometric_scheda === 1 || exercise.is_isometric_scheda === true) {
    return EXERCISE_TYPES.ISOMETRIC;
  }
  
  return EXERCISE_TYPES.NORMAL;
};

/**
 * Restituisce l'unità di misura corretta per il tipo di esercizio
 * @param {string|object} exerciseTypeOrExercise - Tipo esercizio (string) o oggetto esercizio
 * @returns {string} Unità di misura ('ripetizioni', 'secondi', 'minuti')
 */
export const getExerciseUnitLabel = (exerciseTypeOrExercise) => {
  // Se è un oggetto esercizio, determina il tipo
  let exerciseType = exerciseTypeOrExercise;
  if (typeof exerciseTypeOrExercise === 'object') {
    exerciseType = getExerciseType(exerciseTypeOrExercise);
  }
  
  switch (exerciseType?.toLowerCase()) {
    case EXERCISE_TYPES.ISOMETRIC:
      return 'secondi';
    case EXERCISE_TYPES.CARDIO:
      return 'minuti';
    case EXERCISE_TYPES.NORMAL:
    default:
      return 'ripetizioni';
  }
};

/**
 * Formatta il valore in base al tipo di esercizio
 * Per cardio: converte secondi in minuti
 * Per isometrici e normali: restituisce valore originale
 * 
 * @param {number} value - Valore da formattare (sempre in secondi per iso/cardio)
 * @param {string|object} exerciseTypeOrExercise - Tipo esercizio (string) o oggetto esercizio
 * @returns {number} Valore formattato
 */
export const formatExerciseValue = (value, exerciseTypeOrExercise) => {
  // Se è un oggetto esercizio, determina il tipo
  let exerciseType = exerciseTypeOrExercise;
  if (typeof exerciseTypeOrExercise === 'object') {
    exerciseType = getExerciseType(exerciseTypeOrExercise);
  }
  
  if (exerciseType?.toLowerCase() === EXERCISE_TYPES.CARDIO) {
    // Converti secondi in minuti e arrotonda
    return Math.round(value / 60);
  }
  
  // Per isometrici e normali, restituisci valore originale
  return value;
};

/**
 * Formatta completo: valore + unità
 * @param {number} value - Valore da formattare
 * @param {string|object} exerciseTypeOrExercise - Tipo esercizio (string) o oggetto esercizio
 * @returns {string} Es: "60 secondi", "30 minuti", "12 ripetizioni"
 */
export const formatExerciseDisplay = (value, exerciseTypeOrExercise) => {
  const formattedValue = formatExerciseValue(value, exerciseTypeOrExercise);
  const unit = getExerciseUnitLabel(exerciseTypeOrExercise);
  return `${formattedValue} ${unit}`;
};

/**
 * Converte il valore dall'unità visualizzata a quella del database
 * Per cardio: converte minuti in secondi
 * Per isometrici e normali: restituisce valore originale
 * 
 * Usare quando si salva un form dove l'utente ha inserito il valore nell'unità visualizzata
 * 
 * @param {number} displayValue - Valore inserito dall'utente (minuti per cardio, secondi per iso, rip per normal)
 * @param {string|object} exerciseTypeOrExercise - Tipo esercizio (string) o oggetto esercizio
 * @returns {number} Valore da salvare nel database
 */
export const convertToDbValue = (displayValue, exerciseTypeOrExercise) => {
  // Se è un oggetto esercizio, determina il tipo
  let exerciseType = exerciseTypeOrExercise;
  if (typeof exerciseTypeOrExercise === 'object') {
    exerciseType = getExerciseType(exerciseTypeOrExercise);
  }
  
  if (exerciseType?.toLowerCase() === EXERCISE_TYPES.CARDIO) {
    // Converti minuti in secondi
    return displayValue * 60;
  }
  
  // Per isometrici e normali, valore rimane invariato
  return displayValue;
};

/**
 * Restituisce il placeholder appropriato per il tipo di esercizio
 * @param {string|object} exerciseTypeOrExercise - Tipo esercizio (string) o oggetto esercizio
 * @returns {string} Placeholder per input
 */
export const getExercisePlaceholder = (exerciseTypeOrExercise) => {
  // Se è un oggetto esercizio, determina il tipo
  let exerciseType = exerciseTypeOrExercise;
  if (typeof exerciseTypeOrExercise === 'object') {
    exerciseType = getExerciseType(exerciseTypeOrExercise);
  }
  
  switch (exerciseType?.toLowerCase()) {
    case EXERCISE_TYPES.ISOMETRIC:
      return 'Secondi di tenuta (es. 60)';
    case EXERCISE_TYPES.CARDIO:
      return 'Minuti (es. 30)';
    case EXERCISE_TYPES.NORMAL:
    default:
      return 'Numero ripetizioni (es. 12)';
  }
};

/**
 * Verifica se un esercizio è cardio
 * @param {object} exercise - Oggetto esercizio
 * @returns {boolean} True se cardio
 */
export const isCardioExercise = (exercise) => {
  return exercise?.is_cardio === 1 || exercise?.is_cardio === true;
};

/**
 * Verifica se un esercizio è isometrico
 * @param {object} exercise - Oggetto esercizio
 * @returns {boolean} True se isometrico
 */
export const isIsometricExercise = (exercise) => {
  return exercise?.is_isometric === 1 || exercise?.is_isometric === true ||
         exercise?.is_isometric_scheda === 1 || exercise?.is_isometric_scheda === true;
};

/**
 * Verifica se un esercizio è normale (non cardio né isometrico)
 * @param {object} exercise - Oggetto esercizio
 * @returns {boolean} True se normale
 */
export const isNormalExercise = (exercise) => {
  return !isCardioExercise(exercise) && !isIsometricExercise(exercise);
};

// Export default per compatibilità
export default {
  EXERCISE_TYPES,
  getExerciseType,
  getExerciseUnitLabel,
  formatExerciseValue,
  formatExerciseDisplay,
  convertToDbValue,
  getExercisePlaceholder,
  isCardioExercise,
  isIsometricExercise,
  isNormalExercise
};
